import { Immutable } from "immer";
import React from "react";
import SVG from "react-inlinesvg";
import Modal from "react-modal";
import { RetailStore } from "tsi-common-react/src/models/location";
import { SyncStoreID } from "tsi-common-react/src/models/nominals";
import { t } from "ttag";

import iconXClose from "../../../assets/svg/x-close.svg";
import { LocatorSendResults } from "../LocatorSendResults";

const handleSubmitWithErrorRecovery = async (
    recipient: string,
    selected: Set<SyncStoreID>,
    onSubmit: (
        recipient: string,
        selectedStoreIDs: Set<SyncStoreID>,
    ) => Promise<void>,
    setIsSubmitting: (value: boolean) => void,
    setIsOpen: (value: boolean) => void,
): Promise<void> => {
    setIsSubmitting(true);
    try {
        await onSubmit(recipient, selected);
    } catch (e) {
        setIsSubmitting(false);
        setIsOpen(true);
    } finally {
        setIsSubmitting(false);
        setIsOpen(false);
    }
};

export const LocatorSendResultsModal = (props: {
    modalClassName: string;
    bodyOpenClassName?: string;
    buttonLabel: string;
    buttonImgSrc: string;
    buttonImgAlt: string;
    modalTitle: string;
    modalDescription: string | React.ReactNode;
    inputType?: "email" | "phone";
    inputPlaceholder?: string;
    submitButtonLabel: string;
    maxSelected: number;
    stores: Immutable<RetailStore[]>;
    getStoreNumber: (store: Immutable<RetailStore>) => number;
    onSubmit: (
        recipient: string,
        selectedStoreIDs: Set<SyncStoreID>,
    ) => Promise<void>;
}): React.ReactNode => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const handleOpen = () => {
        setIsOpen(true);
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    const handleSubmit = async (
        recipient: string,
        selected: Set<SyncStoreID>,
    ) => {
        await handleSubmitWithErrorRecovery(
            recipient,
            selected,
            props.onSubmit,
            setIsSubmitting,
            setIsOpen,
        );
    };

    return (
        <div
            className={`send__item modal modal-${props.modalClassName} store-locator-survey`}
        >
            <button onClick={handleOpen}>
                <span className="send__label">{props.buttonLabel}</span>
                <span className="send__icon">
                    <img
                        alt={props.buttonImgAlt}
                        src={props.buttonImgSrc}
                        loading="lazy"
                        decoding="async"
                    />
                </span>
            </button>
            <Modal
                aria={{
                    modal: true,
                }}
                style={{
                    content: {
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        maxHeight: "95%",
                        maxWidth: "542px",
                        width: "96%",
                        overflow: "auto",
                        padding: "40px",
                    },
                }}
                contentLabel={props.modalTitle}
                bodyOpenClassName={`ReactModal__Body--open ${props.bodyOpenClassName}`}
                portalClassName="locator"
                className={`locations modal ${props.modalClassName}`}
                isOpen={isOpen}
                onRequestClose={handleClose}
                role="dialog"
            >
                <button
                    aria-label={t`Close`}
                    className="modal-close button--ghost"
                    onClick={handleClose}
                >
                    <SVG
                        aria-hidden="true"
                        className="modal-close__icon"
                        src={iconXClose}
                        title={t`Close Icon`}
                    />
                </button>
                <LocatorSendResults
                    title={props.modalTitle}
                    description={props.modalDescription}
                    inputType={props.inputType}
                    inputPlaceholder={props.inputPlaceholder}
                    submitButtonLabel={props.submitButtonLabel}
                    maxSelected={props.maxSelected}
                    stores={props.stores}
                    getStoreNumber={props.getStoreNumber}
                    isSubmitting={isSubmitting}
                    onCancel={handleClose}
                    onSubmit={handleSubmit}
                />
            </Modal>
        </div>
    );
};
